<template>
    <v-dialog :value="value" max-width="920" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card v-if="campaign && credit">
            <v-card-title class="d-flex align-start">
                <div>
                    <div class="text-h6 font-weight-bold">Adesão ao plano — {{ campaign.client_name }}</div>
                    <div class="text-body-2 grey--text text--darken-1">
                        {{ credit.representing ? `${credit.creditor_name} · ` : "" }}{{ credit.class_name }} · {{ credit.amount_formatted }}
                    </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-stepper v-model="step" vertical class="elevation-0">
                    <v-stepper-step :complete="step > 1" step="1">
                        Ler o plano de recuperação judicial
                        <small>Versão {{ campaign.plan ? campaign.plan.version : "—" }}</small>
                    </v-stepper-step>
                    <v-stepper-content step="1">
                        <p class="mb-3">Leia o plano completo antes de aderir. O documento abre em uma nova aba.</p>
                        <v-btn color="primary" outlined :loading="busy === 'plan'" @click="openPlan">
                            <v-icon left>mdi-file-document-outline</v-icon> Abrir o plano
                        </v-btn>
                        <div v-if="planViewed" class="success--text text-body-2 mt-3"><v-icon small color="success">mdi-check</v-icon> Plano aberto.</div>
                        <div class="mt-4">
                            <v-btn color="primary" elevation="0" :disabled="!planViewed" @click="step = 2">Continuar</v-btn>
                        </div>
                    </v-stepper-content>

                    <v-stepper-step :complete="step > 2" step="2">Confirmar leitura e aceite</v-stepper-step>
                    <v-stepper-content step="2">
                        <v-checkbox v-model="readConfirmed" hide-details class="mt-0"
                            label="Declaro que li integralmente o plano de recuperação judicial."></v-checkbox>
                        <v-checkbox v-model="accepted" hide-details
                            :label="`Declaro que adiro ao plano de recuperação judicial de ${campaign.client_name} em relação a este crédito.`"></v-checkbox>
                        <div class="mt-4">
                            <v-btn text @click="step = 1">Voltar</v-btn>
                            <v-btn color="primary" elevation="0" :disabled="!readConfirmed || !accepted" @click="step = 3">Continuar</v-btn>
                        </div>
                    </v-stepper-content>

                    <v-stepper-step :complete="step > 3" step="3">Escolher a opção de pagamento</v-stepper-step>
                    <v-stepper-content step="3">
                        <v-card outlined class="pa-3 mb-4">
                            <div class="text-caption grey--text">Crédito no Quadro-Geral de Credores</div>
                            <div><strong>{{ credit.class_name }}</strong> · {{ credit.amount_formatted }} · devedora {{ credit.debtor }}</div>
                        </v-card>
                        <v-alert v-if="!credit.payment_options.length" type="warning" outlined dense>
                            Não há opção de pagamento cadastrada para a sua classe. Entre em contato com o Administrador Judicial.
                        </v-alert>
                        <v-radio-group v-model="paymentOptionId" class="mt-0">
                            <v-card v-for="option in credit.payment_options" :key="option.id" outlined class="pa-3 mb-3"
                                :class="{ 'option-selected': paymentOptionId === option.id }" @click="paymentOptionId = option.id">
                                <v-radio :value="option.id" :label="option.title" class="font-weight-bold"></v-radio>
                                <div class="option-text ml-8" v-html="option.content_html"></div>
                            </v-card>
                        </v-radio-group>
                        <v-btn text @click="step = 2">Voltar</v-btn>
                        <v-btn color="primary" elevation="0" :disabled="!paymentOptionId" :loading="busy === 'preview'" @click="loadPreview">Ver prévia do termo</v-btn>
                    </v-stepper-content>

                    <v-stepper-step :complete="step > 4" step="4">Conferir a prévia do termo</v-stepper-step>
                    <v-stepper-content step="4">
                        <div class="term-preview" v-html="previewHtml"></div>
                        <v-alert type="info" outlined dense class="mt-4">
                            Confira seus dados. Se algo estiver errado, corrija em <router-link to="/minha-conta">Minha conta</router-link> antes de emitir o termo.
                        </v-alert>
                        <v-btn text @click="step = 3">Voltar</v-btn>
                        <v-btn color="primary" elevation="0" :loading="busy === 'create'" @click="createTerm">Emitir termo de adesão</v-btn>
                    </v-stepper-content>

                    <v-stepper-step :complete="step > 5" step="5">Baixar e assinar pelo GOV.br</v-stepper-step>
                    <v-stepper-content step="5">
                        <template v-if="term">
                            <p>Termo <strong>{{ term.document_code }}</strong> emitido.</p>
                            <v-btn color="primary" elevation="0" :loading="busy === 'download'" @click="download">
                                <v-icon left>mdi-download</v-icon> Baixar termo (PDF)
                            </v-btn>
                            <v-card v-if="term.signer_kind !== 'pj'" outlined class="pa-4 mt-4">
                                <div class="font-weight-bold mb-2">Como assinar</div>
                                <ol class="text-body-2">
                                    <li>Acesse <a href="https://assinador.iti.br" target="_blank" rel="noopener">assinador.iti.br</a> e entre com a sua conta gov.br (nível prata ou ouro).</li>
                                    <li>Envie o PDF baixado, posicione a assinatura e assine.</li>
                                    <li>Baixe o arquivo assinado e envie no próximo passo.</li>
                                </ol>
                                <div class="text-caption warning--text text--darken-2 mt-2">
                                    Não edite, não imprima e não salve o PDF em outro programa: qualquer alteração invalida o termo. A assinatura precisa ser do CPF {{ term.signer_kind === 'pj' ? "do representante" : "do credor ou do procurador habilitado" }}.
                                </div>
                            </v-card>
                            <v-card v-else outlined class="pa-4 mt-4 text-body-2">
                                Termo em nome de pessoa jurídica: assine conforme os atos societários (assinatura digital do representante ou procurador) e envie no próximo passo. O Administrador Judicial analisa o termo junto com os documentos societários do cadastro.
                            </v-card>
                            <div class="mt-4">
                                <v-btn color="primary" elevation="0" @click="step = 6">Já assinei</v-btn>
                            </div>
                        </template>
                    </v-stepper-content>

                    <v-stepper-step :complete="submitted" step="6">Enviar o termo assinado</v-stepper-step>
                    <v-stepper-content step="6">
                        <template v-if="!submitted">
                            <v-file-input v-model="signedFile" accept="application/pdf,.pdf" label="Termo assinado (PDF, até 10 MB)"
                                outlined dense show-size prepend-icon="" prepend-inner-icon="mdi-file-pdf-box"></v-file-input>
                            <v-alert v-if="submitError" type="error" outlined dense>{{ submitError }}</v-alert>
                            <v-btn text @click="step = 5">Voltar</v-btn>
                            <v-btn color="primary" elevation="0" :disabled="!signedFile" :loading="busy === 'submit'" @click="submit">Enviar</v-btn>
                        </template>
                        <v-alert v-else type="success" outlined>
                            {{ term.status === 'approved'
                                ? "Assinatura GOV.br validada. Sua adesão ao plano foi registrada."
                                : "Termo recebido. Ele será analisado pelo Administrador Judicial; acompanhe o status em Meus termos." }}
                        </v-alert>
                    </v-stepper-content>
                </v-stepper>
            </v-card-text>

            <v-card-actions v-if="submitted">
                <v-spacer></v-spacer>
                <v-btn color="primary" elevation="0" @click="close">Concluir</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import { errorMessage } from "./plan-adhesion.util";

// Fluxo de adesao de um credito: plano -> aceites -> opcao -> previa ->
// emissao/download -> envio do assinado. Com `resumeTerm` o fluxo retoma do
// download (termo ja emitido aguardando assinatura ou rejeitado).
export default {
    name: "PlanAdhesionWizard",

    props: {
        value: { type: Boolean, default: false },
        campaign: { type: Object, default: null },
        credit: { type: Object, default: null },
        resumeTerm: { type: Object, default: null },
    },

    data: () => ({
        step: 1,
        planViewed: false,
        readConfirmed: false,
        accepted: false,
        paymentOptionId: null,
        previewHtml: "",
        term: null,
        signedFile: null,
        submitError: null,
        submitted: false,
        busy: null,
    }),

    watch: {
        value(open) {
            if (open) this.reset();
        },
    },

    methods: {
        reset() {
            this.planViewed = !!(this.campaign && this.campaign.plan_viewed);
            this.readConfirmed = false;
            this.accepted = false;
            this.paymentOptionId = this.credit && this.credit.payment_options.length === 1 ? this.credit.payment_options[0].id : null;
            this.previewHtml = "";
            this.term = this.resumeTerm;
            this.signedFile = null;
            this.submitError = null;
            this.submitted = false;
            this.step = this.resumeTerm ? 5 : 1;
        },

        notifyError(error) {
            this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 6000 });
        },

        close() {
            this.$emit("input", false);
            this.$emit("changed");
        },

        async openPlan() {
            // A aba abre antes da resposta para nao ser bloqueada como pop-up.
            const tab = window.open("", "_blank");
            this.busy = "plan";
            try {
                const result = await axios.get(`/api/plan-adhesions/${this.campaign.id}/plan`);
                if (tab) tab.location = result.data.content.url;
                else window.location.assign(result.data.content.url);
                this.planViewed = true;
            } catch (error) {
                if (tab) tab.close();
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async loadPreview() {
            this.busy = "preview";
            try {
                const result = await axios.post(`/api/plan-adhesions/${this.campaign.id}/preview`, {
                    user_creditor_id: this.credit.user_creditor_id,
                    payment_option_id: this.paymentOptionId,
                });
                this.previewHtml = result.data.content.html;
                this.step = 4;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async createTerm() {
            this.busy = "create";
            try {
                const result = await axios.post(`/api/plan-adhesions/${this.campaign.id}/terms`, {
                    user_creditor_id: this.credit.user_creditor_id,
                    payment_option_id: this.paymentOptionId,
                    read_confirmed: this.readConfirmed,
                    accepted: this.accepted,
                });
                this.term = result.data.content;
                this.step = 5;
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async download() {
            const tab = window.open("", "_blank");
            this.busy = "download";
            try {
                const result = await axios.get(`/api/plan-adhesion-terms/${this.term.id}/file`);
                if (tab) tab.location = result.data.content.url;
                else window.location.assign(result.data.content.url);
            } catch (error) {
                if (tab) tab.close();
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async submit() {
            if (!/\.pdf$/i.test(this.signedFile.name)) {
                this.submitError = "Envie o arquivo PDF assinado.";
                return;
            }
            if (this.signedFile.size > 10 * 1024 * 1024) {
                this.submitError = "O arquivo deve ter no máximo 10 MB.";
                return;
            }
            this.busy = "submit";
            this.submitError = null;
            try {
                const form = new FormData();
                form.append("file", this.signedFile);
                const result = await axios.post(`/api/plan-adhesion-terms/${this.term.id}/submit`, form);
                this.term = result.data.content;
                this.$emit("changed");
                // Rejeitado na validacao automatica: mostra o motivo e permite reenviar.
                if (this.term.status === "rejected") {
                    this.submitError = this.term.validation_message || this.term.rejection_reason || "A assinatura não pôde ser validada.";
                    this.signedFile = null;
                    return;
                }
                this.submitted = true;
            } catch (error) {
                this.submitError = errorMessage(error);
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>

<style scoped>
.option-selected {
    border-color: var(--v-primary-base) !important;
    border-width: 2px;
}

.option-text ::v-deep p {
    margin-bottom: 4px;
}

.term-preview {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 24px;
    max-height: 420px;
    overflow-y: auto;
    background: #fff;
    font-size: 14px;
    line-height: 1.5;
}
</style>
